import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
  AutoCompleteTag,
} from '@choc-ui/chakra-autocomplete';
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/react';
import DepartmentApi from '~/common/api/zeiterfassungServerApi/DepartmentApi';
import useTranslate from '~/common/utils/translate';
import { useField } from 'formik';
import _ from 'lodash';

type EmployeeDepartmentsProps = {
  name: string;
  label?: string;
  isDisabled?: boolean;
};

export function EmployeeDepartments({
  name,
  label,
  isDisabled,
}: EmployeeDepartmentsProps) {
  const [{ data: departments = [], isLoading = [] }] =
    DepartmentApi.hooks.useDepartmentsQuery();

  const { mutateAsync: createDepartment } =
    DepartmentApi.hooks.useCreateDepartment();
  const [field, meta, helpers] = useField<number[]>(name);
  const t = useTranslate();
  return (
    <FormControl isDisabled={isDisabled} isInvalid={!!meta.error}>
      <FormLabel>{t(label)}</FormLabel>
      <AutoComplete
        rollNavigation
        openOnFocus
        multiple
        suggestWhenEmpty
        focusInputOnSelect
        isLoading={isLoading as boolean}
        creatable
        onCreateOption={({ item }) =>
          void createDepartment({
            name: item.value as string,
            description: '',
          }).then((department) =>
            helpers.setValue([
              ...(field.value?.filter((val) => !isNaN(val)) ?? []),
              department.id,
            ]),
          )
        }
        onBlur={() => void field.onBlur({ target: { name } })}
        value={field.value?.map((id) => String(id))}
        filter={(query: string, _value: string, optionLabel: string) => {
          return optionLabel.toLowerCase().includes(query.toLowerCase());
        }}
        onChange={(value: string[]) =>
          void helpers.setValue(value.map((value) => Number(value)))
        }
      >
        <Box
          sx={{
            borderColor: '#807a7a',
            borderWidth: '1px',
            p: '1px',
            borderRadius: 'md',
            '&:hover': {
              borderColor: 'gray.300',
            },
            '&:focus-within': {
              borderColor: 'green.600',
              borderWidth: '2px',
              p: '0px',
            },
          }}
        >
          <AutoCompleteInput
            variant="filled"
            sx={{
              bg: 'inherit',
              '&:hover': {
                bg: 'inherit',
              },
            }}
          >
            {_.orderBy(
              field.value?.map(
                (depId) => departments.find((dep) => dep.id === depId)!,
              ),
              ['name'],
            )
              .filter(Boolean)
              ?.map(({ id: depId, name }) => (
                <AutoCompleteTag
                  key={`tag-${depId}`}
                  label={name}
                  onRemove={() => {
                    const newValue = field.value.filter((id) => id !== depId);
                    void helpers.setValue(newValue);
                  }}
                />
              ))}
          </AutoCompleteInput>
        </Box>
        <AutoCompleteList>
          {_.orderBy(departments, ['name']).map((department, index) => (
            <AutoCompleteItem
              key={`department-complete-option-${index}`}
              value={department.id}
              label={department.name}
            >
              {department.name}
            </AutoCompleteItem>
          ))}
        </AutoCompleteList>
      </AutoComplete>
      {!!meta.error && <FormErrorMessage>{t(meta.error)}</FormErrorMessage>}
    </FormControl>
  );
}
